import React, { useState, useEffect, useCallback } from 'react';
import {
    EyeIcon,
    ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { apiService, AffiliateCommission, AffiliateCommissionStatus } from '../services/api';
import AffiliateCommissionCreateModal from './AffiliateCommissionCreateModal';
import AffiliateCommissionEditModal from './AffiliateCommissionEditModal';
import AffiliateCommissionViewModal from './AffiliateCommissionViewModal';
import ConfirmModal from './ConfirmModal';

interface AffiliateCommissionManagementProps {
    onBack?: () => void;
}

const AffiliateCommissionManagement: React.FC<AffiliateCommissionManagementProps> = ({ onBack }) => {
    const [commissions, setCommissions] = useState<AffiliateCommission[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [isViewModalVisible, setIsViewModalVisible] = useState(false);
    const [selectedCommission, setSelectedCommission] = useState<AffiliateCommission | null>(null);
    const [confirmModal, setConfirmModal] = useState<{
        visible: boolean;
        commission: AffiliateCommission | null;
        message: string;
    }>({
        visible: false,
        commission: null,
        message: '',
    });

    const fetchCommissions = useCallback(async () => {
        setLoading(true);
        try {
            const response = await apiService.getAffiliateCommissions({
                page: currentPage,
                limit: pageSize,
                status: statusFilter !== 'all' ? statusFilter as AffiliateCommissionStatus : undefined,
            });
            setCommissions(response.commissions || []);
            setTotal(response.pagination?.total || 0);
        } catch (error) {
            console.error('Failed to fetch affiliate commissions:', error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, statusFilter]);

    useEffect(() => {
        fetchCommissions();
    }, [fetchCommissions]);

    const handleView = (commission: AffiliateCommission) => {
        setSelectedCommission(commission);
        setIsViewModalVisible(true);
    };

    const confirmDelete = async () => {
        if (!confirmModal.commission) return;
        
        try {
            await apiService.deleteAffiliateCommission(confirmModal.commission.id);
            setConfirmModal({ visible: false, commission: null, message: '' });
            fetchCommissions();
        } catch (error) {
            console.error('Failed to delete commission:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to delete commission. Please try again.';
            alert(errorMessage);
        }
    };

    const handleCreateSuccess = () => {
        setIsCreateModalVisible(false);
        fetchCommissions();
    };

    const handleEditSuccess = () => {
        setIsEditModalVisible(false);
        setSelectedCommission(null);
        fetchCommissions();
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const getStatusColor = (status: AffiliateCommissionStatus) => {
        switch (status) {
            case AffiliateCommissionStatus.PENDING:
                return 'bg-yellow-100 text-yellow-800';
            case AffiliateCommissionStatus.CONFIRMED:
                return 'bg-blue-100 text-blue-800';
            case AffiliateCommissionStatus.PAID:
                return 'bg-green-100 text-green-800';
            case AffiliateCommissionStatus.VOID:
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    {onBack && (
                        <button
                            onClick={onBack}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
                        >
                            <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                    )}
                    <h1 className="text-2xl font-bold text-gray-900">Affiliate Commission Management</h1>
                </div>
                <div className="text-sm text-gray-500">
                    Total Commissions: {total}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-600">Status:</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        >
                            <option value="all">All</option>
                            <option value={AffiliateCommissionStatus.PENDING}>Pending</option>
                            <option value={AffiliateCommissionStatus.CONFIRMED}>Confirmed</option>
                            <option value={AffiliateCommissionStatus.PAID}>Paid</option>
                            <option value={AffiliateCommissionStatus.VOID}>Void</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <label className="text-sm text-gray-600">Page Size:</label>
                        <select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    ID
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Affiliate Member
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Program
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Order
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Amount
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Created At
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                                        Loading...
                                    </td>
                                </tr>
                            ) : commissions.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                                        No commissions found
                                    </td>
                                </tr>
                            ) : (
                                commissions.map((commission) => (
                                    <tr key={commission.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-mono text-gray-900 bg-gray-50 px-2 py-1 rounded border">
                                                {commission.id}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900 text-left">
                                                {commission.affiliateMember?.code || `Member ID: ${commission.affiliateMemberId}`}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 text-left">
                                                {commission.program?.name || `Program ID: ${commission.programId}`}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 font-mono text-left">
                                                {commission.order?.orderNumber || `Order ID: ${commission.orderId}`}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-semibold text-gray-900 text-left">
                                                {formatCurrency(commission.commissionAmount)}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-left">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(commission.status)}`}>
                                                {commission.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 text-left">
                                                {new Date(commission.createdAt).toLocaleDateString('id-ID', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                })}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={() => handleView(commission)}
                                                    className="text-blue-600 hover:text-blue-900 p-1"
                                                    title="View Details"
                                                >
                                                    <EyeIcon className="w-4 h-4" />
                                                </button>
                                                {/* Edit/Delete actions removed per request */}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {total > 0 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                                disabled={currentPage >= Math.ceil(total / pageSize)}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                                    <span className="font-medium">
                                        {Math.min(currentPage * pageSize, total)}
                                    </span>{' '}
                                    of <span className="font-medium">{total}</span> results
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Previous
                                    </button>
                                    <button
                                        onClick={() => setCurrentPage(Math.min(Math.ceil(total / pageSize), currentPage + 1))}
                                        disabled={currentPage >= Math.ceil(total / pageSize)}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modals */}
            <AffiliateCommissionCreateModal
                visible={isCreateModalVisible}
                onCancel={() => setIsCreateModalVisible(false)}
                onSuccess={handleCreateSuccess}
            />

            <AffiliateCommissionEditModal
                visible={isEditModalVisible}
                commission={selectedCommission}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    setSelectedCommission(null);
                }}
                onSuccess={handleEditSuccess}
            />

            <AffiliateCommissionViewModal
                visible={isViewModalVisible}
                commission={selectedCommission}
                onCancel={() => {
                    setIsViewModalVisible(false);
                    setSelectedCommission(null);
                }}
            />

            <ConfirmModal
                visible={confirmModal.visible}
                title="Confirm Delete"
                message={confirmModal.message}
                confirmText="Delete"
                cancelText="Cancel"
                confirmButtonClass="bg-red-600 hover:bg-red-700"
                onConfirm={confirmDelete}
                onCancel={() => setConfirmModal({ visible: false, commission: null, message: '' })}
            />
        </div>
    );
};

export default AffiliateCommissionManagement;

